import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import "./thankyou.css";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

function readStoredUser() {
  try {
    const raw =
      localStorage.getItem("userData") || sessionStorage.getItem("userData") || "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function resolveLobby(locationState) {
  const fromState =
    typeof locationState?.lobbyName === "string" ? locationState.lobbyName.trim() : "";
  if (fromState) return fromState;
  try {
    const ss = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (ss && ss.trim()) return ss.trim();
  } catch {
    // ignore storage failures
  }
  return "";
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : fallback;
}

function toTimeText(value, fallback = "00:00") {
  const s = String(value ?? "").trim();
  return s || fallback;
}

/**
 * Same folder shape as lobby `CreateFolderSvg` — tab top-left, diagonal join.
 * viewBox 0 0 300 240
 */
function ThankYouFeedbackFolderSvg() {
  return (
    <svg
      className="thankyou-feedback-folder-svg"
      viewBox="0 0 300 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path
        d="
          M12,0
          H148
          L168,36
          H288 Q300,36 300,48
          V228 Q300,240 288,240
          H12 Q0,240 0,228
          V12 Q0,0 12,0 Z
        "
        fill="#ffffff"
      />
    </svg>
  );
}

/** Same path as lobby `CreateFolderSvg` — filled white, tab top-left, diagonal join. */
function ThankYouScorePointsFolderSvg() {
  return (
    <svg
      className="thankyou-score-folder-svg"
      viewBox="0 0 300 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path
        d="
          M12,0
          H148
          L168,36
          H288 Q300,36 300,48
          V228 Q300,240 288,240
          H12 Q0,240 0,228
          V12 Q0,0 12,0 Z
        "
        fill="#ffffff"
      />
    </svg>
  );
}

/** Same path as lobby `JoinFolderSvg` — outline, tab top-right, diagonal join. */
function ThankYouScoreTimeFolderSvg() {
  return (
    <svg
      className="thankyou-score-folder-svg"
      viewBox="0 0 300 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path
        d="
          M12,240
          Q0,240 0,228
          V48 Q0,36 12,36
          H126
          L146,0
          H288 Q300,0 300,12
          V228 Q300,240 288,240
          Z
        "
        fill="none"
        stroke="rgba(255,255,255,0.95)"
        strokeWidth="4"
      />
    </svg>
  );
}

function ThankYou() {
  const location = useLocation();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const userData = useMemo(() => readStoredUser(), []);
  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );
  const lobbyName = useMemo(() => resolveLobby(location.state), [location.state]);

  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(() => toNumber(location.state?.points, 0));
  const [timeTaken, setTimeTaken] = useState(() => toTimeText(location.state?.time, "00:00"));
  const [stageRowId, setStageRowId] = useState("");
  const [view, setView] = useState("feedback");
  const [feedbackError, setFeedbackError] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [hoverSide, setHoverSide] = useState(null);
  const [pickedSide, setPickedSide] = useState(null);
  const reportIdRef = useRef("");
  const reportSyncDoneRef = useRef(false);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const hadCommon = body.classList.contains("common-bg");
    html.classList.add("thank-you-static-bg");
    body.classList.add("thank-you-static-bg");
    body.classList.remove("common-bg");
    return () => {
      html.classList.remove("thank-you-static-bg");
      body.classList.remove("thank-you-static-bg");
      if (hadCommon) body.classList.add("common-bg");
    };
  }, []);

  useEffect(() => {
    const userId = userData.userId || userData.userid || userData.id;
    const sessionId = userData.sessionId;
    const organizationId = userData.organizationId;
    if (!backendBase || !adminToken || !userId || !sessionId || !organizationId) {
      setLoading(false);
      return;
    }

    const loadReport = async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId,
            sessionId,
            organizationId,
            lobby: lobbyName || "",
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.success && data?.report) {
          setScore(toNumber(data.report.points, 0));
          setTimeTaken(toTimeText(data.report.time, "00:00"));
          if (data.report.id != null && data.report.id !== "") {
            setStageRowId(String(data.report.id));
          }
          if (data.report.reportId != null && data.report.reportId !== "") {
            reportIdRef.current = String(data.report.reportId);
          }
          const existingFeedback = Number(data.report.feedback);
          if (Number.isFinite(existingFeedback) && existingFeedback >= 1 && existingFeedback <= 5) {
            setView("score");
          }
        }
      } catch (err) {
        console.error("fetchReport failed:", err);
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [adminToken, backendBase, lobbyName, userData]);

  useEffect(() => {
    if (reportSyncDoneRef.current) return;
    const userId = userData.userId || userData.userid || userData.id;
    const sessionId = userData.sessionId;
    const organizationId = userData.organizationId;
    if (!backendBase || !adminToken || !userId || !sessionId || !organizationId) return;
    if (loading) return;
    const roleForApi =
      userData.user_type ||
      userData.userType ||
      userData.role ||
      (userData.source === "DEMO" ? "demobypass" : null) ||
      userData.source ||
      "ORG_USER";
    if (String(roleForApi).toLowerCase() === "demobypass") return;

    (async () => {
      try {
        const payload = {
          ...(reportIdRef.current ? { reportId: reportIdRef.current } : {}),
          sessionId: String(sessionId),
          organizationId: String(organizationId),
          role: roleForApi,
          token: userData.token || "",
          points: Number(score) || 0,
          time: toTimeText(timeTaken, "00:00"),
          gameId: userData.gameId,
          name:
            (typeof userData.name === "string" && userData.name.trim()) ||
            [userData.firstName, userData.lastName].filter(Boolean).join(" ").trim() ||
            "",
          userId: String(userId),
        };
        const reportRes = await sendReport(payload);
        const rid = extractReportIdFromResponse(reportRes);
        if (rid) {
          reportIdRef.current = String(rid);
          if (stageRowId) {
            try {
              await fetch(`${backendBase}/welcomeStageLinkReport`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${adminToken}`,
                },
                body: JSON.stringify({
                  userId,
                  sessionId,
                  organizationId,
                  stageId: stageRowId,
                  reportId: String(rid),
                }),
              });
            } catch (linkErr) {
              console.error("thankyou welcomeStageLinkReport:", linkErr);
            }
          }
        }
      } catch (err) {
        console.error("thankyou sendReport:", err);
      } finally {
        reportSyncDoneRef.current = true;
      }
    })();
  }, [adminToken, backendBase, loading, score, stageRowId, timeTaken, userData]);

  const commitFeedback = useCallback(
    async (feedbackNumeric) => {
      const userId = userData.userId || userData.userid || userData.id;
      const sessionId = userData.sessionId;
      const organizationId = userData.organizationId;
      if (!backendBase || !adminToken || !userId || !sessionId || !organizationId) {
        throw new Error("Missing session details. Please relaunch the game.");
      }
      const res = await fetch(`${backendBase}/welcomeStageFeedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId,
          sessionId,
          organizationId,
          stageId: stageRowId,
          feedback: feedbackNumeric,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Could not save feedback.");
      }
    },
    [adminToken, backendBase, stageRowId, userData]
  );

  /** Same flow as game question: brief pick highlight, then commit. */
  const handleFeedbackPick = useCallback(
    (side, feedbackNumeric) => {
      if (savingFeedback) return;
      if (!stageRowId) {
        setFeedbackError("Could not find stage row. Please try again.");
        return;
      }
      setPickedSide(side);
      setHoverSide(null);
      setSavingFeedback(true);
      setFeedbackError("");
      window.setTimeout(() => {
        void (async () => {
          try {
            await commitFeedback(feedbackNumeric);
            setView("score");
          } catch (err) {
            setFeedbackError(err?.message || "Could not save feedback.");
          } finally {
            setSavingFeedback(false);
            setPickedSide(null);
          }
        })();
      }, 380);
    },
    [commitFeedback, savingFeedback, stageRowId]
  );

  const splitClass = pickedSide
    ? `thankyou-game-split-bg thankyou-game-split-bg--picked-${pickedSide}`
    : hoverSide
    ? `thankyou-game-split-bg thankyou-game-split-bg--hover-${hoverSide}`
    : "thankyou-game-split-bg";

  return (
    <div className="thankyou-score-page">
      {view === "feedback" ? (
        <div className="thankyou-game-root">
          <div className={splitClass} aria-hidden="true">
            <div className="thankyou-game-split-left" />
            <div className="thankyou-game-split-right" />
          </div>
          <div className="thankyou-game-wave" aria-hidden="true" />

          <div className="thankyou-game-surface">
            <div className="thankyou-game-stage">
              <div className="thankyou-game-row">
                <div className="thankyou-game-center">
                  <div className="thankyou-feedback-folder">
                    <ThankYouFeedbackFolderSvg />
                    <span className="thankyou-feedback-folder-tab-text">Feedback</span>
                    <div className="thankyou-feedback-folder-body">
                      <p className="thankyou-feedback-text">"you loved the game"</p>
                    </div>
                  </div>
                </div>

                <div className="thankyou-game-choices-row">
                  <button
                    type="button"
                    className="thankyou-game-pill thankyou-game-pill--left"
                    disabled={savingFeedback}
                    onMouseEnter={() => setHoverSide("left")}
                    onMouseLeave={() => setHoverSide(null)}
                    onFocus={() => setHoverSide("left")}
                    onBlur={() => setHoverSide(null)}
                    onClick={() => handleFeedbackPick("left", 1)}
                  >
                    MYTH
                  </button>

                  <button
                    type="button"
                    className="thankyou-game-pill thankyou-game-pill--right"
                    disabled={savingFeedback}
                    onMouseEnter={() => setHoverSide("right")}
                    onMouseLeave={() => setHoverSide(null)}
                    onFocus={() => setHoverSide("right")}
                    onBlur={() => setHoverSide(null)}
                    onClick={() => handleFeedbackPick("right", 5)}
                  >
                    FACT
                  </button>
                </div>
              </div>
            </div>

            {feedbackError ? (
              <p className="thankyou-feedback-error thankyou-feedback-error--surface">
                {feedbackError}
              </p>
            ) : null}
            {savingFeedback ? (
              <p className="thankyou-feedback-saving" aria-live="polite">
               
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="thankyou-score-stage-root">
          <div className="thankyou-game-split-bg" aria-hidden="true">
            <div className="thankyou-game-split-left" />
            <div className="thankyou-game-split-right" />
          </div>

          <div className="thankyou-score-surface">
            <h1 className="thankyou-score-title">SCORE</h1>

            <div className="thankyou-score-grid">
              <div className="thankyou-score-folder thankyou-score-folder--filled">
                <ThankYouScorePointsFolderSvg />
                <div className="thankyou-score-folder-body">
                  <p className="thankyou-score-folder-label">Points</p>
                  <p className="thankyou-score-folder-value">{loading ? "…" : score}</p>
                </div>
              </div>

              <div className="thankyou-score-folder thankyou-score-folder--outline">
                <ThankYouScoreTimeFolderSvg />
                <div className="thankyou-score-folder-body">
                  <p className="thankyou-score-folder-label">Time Taken</p>
                  <p className="thankyou-score-folder-value">{loading ? "…" : timeTaken}</p>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="thankyou-leaderboard-btn"
              onClick={() =>
                navigate("/leaderboard", {
                  replace: false,
                  state: { lobbyName },
                })
              }
            >
              Leaderboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ThankYou;
